// Compatibility wrapper to robustly resolve the Froala React component
// across different bundlers (CJS/ESM) and Vite prebundling shapes.
import React, { useEffect, useImperativeHandle, useRef } from "react";
import * as FroalaNS from "react-froala-wysiwyg";

function resolveFroalaComponent(ns: any): any {
  // Common candidates
  let Comp = ns?.default;
  if (typeof Comp !== "function") {
    if (typeof ns?.FroalaEditor === "function") Comp = ns.FroalaEditor;
    else if (typeof ns?.ReactFroalaWysiwyg === "function") Comp = ns.ReactFroalaWysiwyg;
  }
  // Sometimes the module itself is a function (CJS default)
  if (typeof Comp !== "function" && typeof ns === "function") {
    Comp = ns;
  }
  // As a last resort, pick the first function export
  if (typeof Comp !== "function") {
    const fn = Object.values(ns).find((v: any) => typeof v === "function");
    if (fn) Comp = fn;
  }
  if (typeof Comp !== "function") {
    // Give a helpful error for better debugging in runtime
    throw new Error("Failed to resolve react-froala-wysiwyg component export. Received keys: " + Object.keys(ns).join(", "));
  }
  return Comp;
}

let FroalaEditorCompat: any;
try {
  FroalaEditorCompat = resolveFroalaComponent(FroalaNS as any);
} catch (e) {
  const Fallback: any = React.forwardRef<any, any>(function FroalaFallback(props, ref) {
    const { model, onModelChange, ...rest } = props || {};
    const divRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => ({
      getEditor() {
        const el = divRef.current;
        if (!el) return null;
        return {
          el,
          html: {
            get: () => el.innerHTML,
            set: (html: string) => {
              el.innerHTML = html || "";
            }
          },
          events: {
            trigger: (_name: string) => { /* no-op */ }
          }
        };
      },
      initialize() { /* no-op */ },
      destroy() { /* no-op */ }
    }));

    useEffect(() => {
      if (divRef.current != null && typeof model === "string") {
        divRef.current.innerHTML = model;
      }
    }, [model]);

    useEffect(() => {
      const el = divRef.current;
      if (!el || !onModelChange) return;
      const handler = () => onModelChange(el.innerHTML || "");
      el.addEventListener("input", handler);
      return () => el.removeEventListener("input", handler);
    }, [onModelChange]);

    return React.createElement("div", { contentEditable: true, ref: divRef, className: "froala-editor-fallback", ...rest });
  });

  FroalaEditorCompat = Fallback;
}

export default FroalaEditorCompat;
